/** App-origin URLs the Service Worker serves straight from IndexedDB.
 *
 *  When a flight is cached the viewer is handed these instead of presigned R2
 *  URLs, so playback does not depend on the presign's shape or its expiry and
 *  a cache miss is an obvious 404 rather than an R2 403. Only the two video
 *  assets need this: everything else the viewer fetches (track, panoramas,
 *  frames) still arrives via presigned URLs that fingerprint.ts recognises. */

export const VIRTUAL_ASSET_PREFIX = "/vmc-offline/flights/";

/** The origin this code is running on — the page's in the app, the SW's
 *  registration scope inside the worker. `undefined` outside a browser (unit
 *  tests), where callers must pass an explicit origin. Shared by
 *  fingerprint.ts and patterns.ts so the two agree on what "same origin"
 *  means. */
export function defaultSelfOrigin(): string | undefined {
  return (globalThis as { location?: { origin?: string } }).location?.origin;
}

export type VirtualAssetKind = "video" | "preview";

export function virtualAssetUrl(origin: string, flightId: string, kind: VirtualAssetKind): string {
  return `${origin}${VIRTUAL_ASSET_PREFIX}${encodeURIComponent(flightId)}/${kind}`;
}

const VIRTUAL_PATH = /^\/vmc-offline\/flights\/([^/]+)\/(video|preview)$/;

export function parseVirtualAssetPath(
  pathname: string,
): { flightId: string; kind: VirtualAssetKind } | null {
  const m = VIRTUAL_PATH.exec(pathname);
  if (m === null) return null;
  let flightId: string;
  try {
    flightId = decodeURIComponent(m[1]!);
  } catch {
    // URIError: a stray "%" or a truncated escape. Callers are the SW's fetch
    // handler and isInterceptable, both of which must treat a malformed path
    // as "not one of ours" rather than let the exception escape.
    return null;
  }
  return { flightId, kind: m[2] as VirtualAssetKind };
}
